import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Dropdown, Modal } from "react-bootstrap";
import { Chat, LocationOn, Send } from "@mui/icons-material";
import moment from "moment";
import next_btn from "../../assets/img/next_btn.png";
import nannyicon from "../../assets/img/nany_icon.png";
import socket from "../../utils/socket";
import { containsContactInfo } from "../../utils/help";
import founderimg from '../../assets/img/founder.webp';
import {
  NannyupcommingUserList,
  clearData as clearUpcomingData,
} from "../../store/apiSlice/NannyUpcomingSlice";
import { UpdateBookingStatus } from "../../store/apiSlice/UpdateBookingStatusSlice";
import { clearChat, getChatHistory } from "../../store/apiSlice/ChatSlice";
import { getUserprofile } from "../../store/apiSlice/UserprofileSlice";
import { uploadFile } from "../../store/apiSlice/uploadFileSlice";
import { ClipLoader } from "react-spinners";

const STATUS_LABELS = {
  0: "Pending",
  1: "Accepted",
  2: "On The Way",
  3: "Reached",
  4: "Completed",
  5: "Cancelled",
  6: "Rejected",
};

const UpcomingNanny = () => {
  const msgEndRef = useRef(null);
  const dispatch = useDispatch();

  const profileData = useSelector(
    (state) => state.rootReducer.UserProfileReducer.data
  );
  const ongoingData = useSelector(
    (state) => state.rootReducer.NannyupcommingReducer.data
  );
  const updateBookstatus = useSelector(
    (state) => state.rootReducer.UpdateBookingStatusReducer.data
  );
  const chatData = useSelector(
    (state) => state.rootReducer.chatHistoryReducer.data
  );
  const uploadFileData = useSelector(
    (state) => state.rootReducer.uploadFileReducer.data
  );

  const currentDate = new Date();
  const formattedDate = currentDate.toISOString();
  const userId = localStorage.getItem("userId");
  const type = localStorage.getItem("type");

  const [message, setMessage] = useState("");
  const [chatIndex, setChatIndex] = useState(null);
  const [show, setShow] = useState(false);
  const [myChatData, setChatData] = useState([]);
  const [dataList, setDataList] = useState(null);
  const [attachment, setAttachment] = useState(null);
  const [uploading, setUploading] = useState(false);

  // === FETCH USER PROFILE ===
  useEffect(() => {
    dispatch(getUserprofile());
  }, [dispatch]);

  // === FETCH UPCOMING BOOKINGS ===
  useEffect(() => {
    dispatch(NannyupcommingUserList(0));
    return () => {
      dispatch(clearUpcomingData());
      setChatIndex(null);
      setShow(false);
    };
  }, [dispatch, updateBookstatus]);

  // === SOCKET CONNECTION ===
  useEffect(() => {
    if (!socket.connected) {
      socket.connect();
      socket.emit("touch_server", { userId });
    }

    socket.on("booking_status_changed", () => {
      dispatch(NannyupcommingUserList(0));
    });

    socket.on("receive_message", (data) => {
      setChatData((prev) => [...prev, data]);
    });

    return () => {
      socket.off("booking_status_changed");
      socket.off("receive_message");
      clearChat();
      setShow(false);
    };
  }, [dispatch, userId]);

  // === SET CHAT DATA ===
  useEffect(() => {
    if (chatData && chatData.status === 1) {
      setShow(true);
      setChatData(chatData.data);
    }
  }, [chatData]);

  // === SET ONGOING BOOKINGS ===
  useEffect(() => {
    if (ongoingData && ongoingData.status === 1) {
      // setDataList(ongoingData.data.data);
      const filteredData = ongoingData.data.data.filter(
        item => !(item.isActive === 0 && (item.type === 0 || item.type === 1 || item.type === 2))
      );

      setDataList(filteredData);
    }
  }, [ongoingData]);

  // === AUTO SCROLL CHAT ===
  useEffect(() => {
    if (show) scrollToBottom();
  }, [myChatData, show]);

  // === PREVIEW FILE ===
  const previewFile = (file) => {
    const type = file.type;
    const name = file.name;
    const localUrl = URL.createObjectURL(file);
    return { type, name, localUrl };
  };

  // === HANDLE FILE UPLOAD ===
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const preview = previewFile(file);
    setAttachment(preview); // temporary preview
    setUploading(true);
    dispatch(uploadFile(file));
  };

  // === DISCARD ATTACHMENT ===
  const discardAttachment = () => {
    setAttachment(null);
    setUploading(false);
  };

  // === SET UPLOADED FILE FROM REDUX ===
  useEffect(() => {
    if (!attachment) return; // prevent overriding when discarded
    if (uploadFileData && uploading) {
      setAttachment((prev) => ({
        ...prev,
        url: uploadFileData.Location,
        uploaded: true,
      }));
      setUploading(false);
    }
  }, [uploadFileData, uploading, attachment]);

  // === SEND MESSAGE ===
  const sendMessage = () => {
    if (!message.trim() && !attachment?.uploaded) return;

    if (containsContactInfo(message)) {
      alert("Sharing contact information is not allowed.");
      return;
    }

    const msgData = {
      bookingId: dataList[chatIndex]._id,
      userId,
      otherUserId:
        type === "1"
          ? dataList[chatIndex].userId
          : dataList[chatIndex].nannyId._id,
      message: message || "",
      attachment: attachment?.uploaded ? attachment.url : null,
      attachmentType: attachment?.uploaded ? attachment.type : null,
      createdAt: formattedDate,
    };

    socket.emit("send_message", msgData);

    setChatData((prev) => [
      ...prev,
      {
        ...msgData,
        senderId: { _id: userId },
      },
    ]);

    setMessage("");
    setAttachment(null);
  };

  // === DATE FORMATTER ===
  const getFormattedDateTime = (dateString) => {
    const now = moment();
    const messageTime = moment.utc(dateString).local();
    if (now.isSame(messageTime, "day")) {
      return messageTime.format("hh:mm A");
    } else if (now.subtract(1, "day").isSame(messageTime, "day")) {
      return "Yesterday";
    } else if (now.isSame(messageTime, "year")) {
      return messageTime.format("D MMM hh:mm A");
    } else {
      return messageTime.format("D MMM YYYY");
    }
  };

  const scrollToBottom = () => {
    msgEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // === FETCH CHAT ===
  const getChat = (id) => {
    const payload = { skip: 0, limit: 100, id };
    dispatch(getChatHistory(payload));
  };

  const openChat = (id, index) => {
    getChat(id);
    setChatIndex(index);
  };

  // === UPDATE BOOKING STATUS ===
  const updateBookingStatusApi = (statusVal, bookingId) => {
    const payload = { bookingId, status: statusVal };
    dispatch(UpdateBookingStatus(payload));
  };

  // === MAIN RETURN ===
  return (
    <>
      <div className="row all_order_box">
        {dataList && dataList.length > 0 ? (
          dataList.map((item, index) => (
            <div className="col-md-6" key={index}>
              <div className="card nany_orders my-2">
                <div className="card.body d-flex">
                  <div className="card.img mr-2">
                    <img
                      alt="card"
                      src={item?.userId?.profileImage || ""}
                      className="rounded-circle"
                    />
                  </div>
                  <div>
                    <div className="card-title">
                      <h6>
                        {item?.userId?.firstName} {item?.userId?.lastName}
                      </h6>
                      <p className="text-sm-end text-md-start text-lg-end">
                        ${item?.price}
                      </p>
                    </div>
                    <div className="Card.Text">
                      <div className="nanny-info">
                        <div className="location mb-1 d-flex align-items-center">
                          <LocationOn /> <span>{item.address}</span>
                        </div>
                        <p className="time mb-0 text-truncate">
                          {item.date} <span>{item.time}</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {item && item.isActive === 1 ? <div>
                  <div className="upcoming_option mt-3 pt-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        {item.status === 1 ||
                          item.status === 2 ||
                          item.status === 3 ? (
                          <Dropdown>
                            <Dropdown.Toggle
                              id="dropdown-basic"
                              className="btn-ongiing"
                            >
                              {item.status === 1
                                ? "Request Accepted"
                                : item.status === 2
                                  ? "On The Way"
                                  : "Reached"}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              <Dropdown.Item
                                onClick={() =>
                                  updateBookingStatusApi(2, item._id)
                                }
                              >
                                On The Way
                              </Dropdown.Item>
                              <Dropdown.Item
                                onClick={() =>
                                  updateBookingStatusApi(3, item._id)
                                }
                              >
                                Reached
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        ) : (
                          <button
                            type="button"
                            className="mb-0 main-button"
                            onClick={() => {
                              item.status === 0
                                ? updateBookingStatusApi(1, item._id)
                                : updateBookingStatusApi(5, item._id);
                            }}
                          >
                            {/* {item.status === 0 ? "Accept Request" : "Canceled"} */}
                            <span
                              className={`status-badge status-${item.status === 0 ? 1 : 5}`}
                            >
                              {item.status === 0 ? "Accept Request" : "Canceled"}
                            </span>
                          </button>
                        )}
                      </div>
                      {item.status !== 0 ? (
                        <div className="upcoming_btns d-flex align-items-center">
                          <Button
                            type="button"
                            className="map_btn"
                            onClick={() => {
                              const lat = item.latitude;
                              const lng = item.longitude;
                              const address = item.address;
                              if (lat && lng) {
                                const url = `https://www.google.com/maps?q=${lat},${lng} (${address})`;
                                window.open(url, "_blank");
                              } else {
                                alert("Location not available");
                              }
                            }}
                          >
                            <img src={next_btn} alt="logo" />
                          </Button>
                          <Button
                            type="button"
                            onClick={() => openChat(item._id, index)}
                            className="map_btn"
                          >
                            <Chat />
                          </Button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="mb-0 btn-cancel-nany"
                          onClick={() => updateBookingStatusApi(6, item._id)}
                        >
                          {/* Reject Request */}
                          <span
                            className={`status-badge status-6`}
                          >
                            {"Reject Request"}
                          </span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                  :
                  // <div className="upcoming_option mt-3 pt-3">
                  //   <div className="d-flex justify-content-center align-items-center">
                  //     <div>
                  //       <span
                  //         className="px-3 py-2"
                  //         style={{
                  //           color: "#d32f2f",
                  //           fontSize: "13px",
                  //           fontWeight: 600,
                  //         }}
                  //       >
                  //         {item.status === 5 ? "Booking Cancelled" : "Boking Rejected"}
                  //       </span>
                  //     </div>
                  //   </div>
                  // </div>

                  <div className='ordered_persons'>
                    <div className="nanny-small-info mt-2">
                      <div className="d-flex justify-content-between align-items-center">

                        <div className="d-flex align-items-center">
                          <img
                            src={founderimg}
                            alt="nanny"
                            className="rounded-circle me-2"
                            width={40}
                            height={40}
                          />
                          <h4 className="mb-0">{item.categoryId?.name}</h4>
                        </div>

                        {/* RIGHT: STATUS */}
                        <span
                          className={`status-badge status-${item.status ?? 0}`}
                        >
                          {STATUS_LABELS[item.status] || "Pending"}
                        </span>

                      </div>
                    </div>
                  </div>
                }
              </div>
            </div>
          ))
        ) : (
          <p style={{ fontWeight: 600 }}>No Bookings Found</p>
        )}
      </div>

      {/* === CHAT MODAL === */}
      {show && chatIndex !== null && (
        <Modal
          show={show}
          onHide={() => setShow(false)}
          size="lg"
          centered
          className="modal_address change_time chat_modal"
        >
          <Modal.Header closeButton className="p-2">
            <Modal.Title className="chat_heading">
              <img src={nannyicon} alt="icon" />
            </Modal.Title>
          </Modal.Header>

          <Modal.Body
            style={{
              padding: 0,
              display: "flex",
              flexDirection: "column",
              height: "70vh",
              backgroundColor: "#f9f9f9",
              borderRadius: "8px",
            }}
          >
            {/* Chat Messages */}
            <ul
              style={{
                listStyle: "none",
                margin: 0,
                padding: "1rem",
                flex: 1,
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: "12px",
              }}
            >
              {myChatData.map((item, index) => {
                const isSender = item.senderId._id === userId;
                return (
                  <li
                    key={index}
                    style={{
                      alignSelf: isSender ? "flex-end" : "flex-start",
                      maxWidth: "75%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: isSender ? "#89d6ce" : "#e5e5ea",
                        color: isSender ? "#fff" : "#000",
                        borderRadius: "20px",
                        padding: "10px 14px",
                        wordBreak: "break-word",
                        fontSize: "14px",
                      }}
                    >
                      {item.message && <div>{item.message}</div>}

                      {item.attachment && (
                        <div style={{ marginTop: "8px" }}>
                          {item.attachmentType?.startsWith("image/") && (
                            <img
                              src={item.attachment}
                              alt="attachment"
                              style={{ maxWidth: "200px", borderRadius: "10px" }}
                            />
                          )}

                          {item.attachmentType?.startsWith("video/") && (
                            <video
                              controls
                              src={item.attachment}
                              style={{ maxWidth: "250px", borderRadius: "10px" }}
                            />
                          )}

                          {item.attachmentType?.includes("pdf") && (
                            <a
                              href={item.attachment}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: "#007bff", fontWeight: 600 }}
                            >
                              📄 View PDF
                            </a>
                          )}

                          {(item.attachmentType?.includes("doc") ||
                            item.attachmentType?.includes("officedocument") ||
                            item.attachmentType?.includes("text") ||
                            item.attachmentType?.includes("sheet")) && (
                              <a
                                href={item.attachment}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: "#007bff", fontWeight: 600 }}
                              >
                                📎 View Document
                              </a>
                            )}
                        </div>
                      )}
                    </div>

                    <span
                      style={{
                        fontSize: "12px",
                        color: "#888",
                        marginTop: "4px",
                        alignSelf: isSender ? "flex-end" : "flex-start",
                      }}
                    >
                      {getFormattedDateTime(item.createdAt)}
                    </span>
                  </li>
                );
              })}
              <div ref={msgEndRef}></div>
            </ul>

            {/* Input Section */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                borderTop: "1px solid #ddd",
                backgroundColor: "#fff",
                padding: "10px",
              }}
            >
              {/* Attachment Preview */}
              {attachment && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    marginBottom: "8px",
                    background: "#f1f1f1",
                    borderRadius: "10px",
                    padding: "8px 12px",
                  }}
                >
                  {uploading ? (
                    <ClipLoader size={18} />
                  ) : attachment.type.startsWith("image/") ? (
                    <img
                      src={attachment.localUrl}
                      alt="preview"
                      style={{ width: "50px", height: "50px", borderRadius: "6px" }}
                    />
                  ) : attachment.type.startsWith("video/") ? (
                    <video
                      src={attachment.localUrl}
                      style={{ width: "60px", height: "40px", borderRadius: "6px" }}
                      muted
                    />
                  ) : (
                    <span>📎 {attachment.name}</span>
                  )}
                  <button
                    onClick={discardAttachment}
                    style={{
                      background: "none",
                      border: "none",
                      fontSize: "16px",
                      color: "#ff4d4f",
                      cursor: "pointer",
                    }}
                    title="Remove file"
                  >
                    ❌
                  </button>
                </div>
              )}

              {/* Message Row */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <label htmlFor="file-upload" style={{ cursor: "pointer" }} title="Attach file">
                  📎
                </label>
                <input
                  id="file-upload"
                  type="file"
                  accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />

                <input
                  type="text"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (message.trim() || attachment?.uploaded))
                      sendMessage();
                  }}
                  placeholder={
                    uploading ? "Uploading file..." : "Type your message..."
                  }
                  disabled={uploading}
                  style={{
                    flex: 1,
                    border: "1px solid #ccc",
                    borderRadius: "20px",
                    padding: "8px 14px",
                    fontSize: "14px",
                    outline: "none",
                  }}
                />

                <button
                  type="button"
                  onClick={sendMessage}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#007bff",
                    marginLeft: "5px",
                    cursor: "pointer",
                    fontSize: "24px",
                  }}
                  disabled={uploading || (!message.trim() && !attachment?.uploaded)}
                >
                  {uploading ? <ClipLoader size={18} /> : <Send />}
                </button>
              </div>
            </div>
          </Modal.Body>
        </Modal>
      )}
    </>
  );
};

export default UpcomingNanny;
